/**
 * intakeFileRouting — route dropped/picked files to the right import flow:
 * spreadsheets → ImportDialog (column mapping), scans → ScanImportDialog (OCR+AI),
 * documents → RecognizeDocumentDialog (single-file recognition).
 */
const SPREADSHEET_EXT = ['csv', 'xlsx', 'xls']
const SCAN_EXT = ['png', 'jpg', 'jpeg', 'webp', 'tif', 'tiff', 'bmp', 'gif', 'pdf']

export const IMPORT_ACCEPT = [...SPREADSHEET_EXT, ...SCAN_EXT].map((e) => `.${e}`).join(',')

export type ImportRouteKind =
  | 'spreadsheet'
  | 'scans'
  | 'mixed'
  | 'unsupported'
  | 'empty'
  | 'multiple-spreadsheets'

export interface ImportRoute {
  kind: ImportRouteKind
  spreadsheet?: File
  scans?: File[]
}

export type ObjectImportKind = 'catalog' | 'document' | 'other'

export type TypedImportRoute =
  | { kind: 'empty' }
  | { kind: 'unsupported-object' }
  | { kind: 'catalog-spreadsheet'; spreadsheet: File }
  | { kind: 'catalog-scans-rejected' }
  | { kind: 'document-file'; file: File }
  | { kind: 'document-multi-rejected' }

function ext(file: File): string {
  return (file.name.split('.').pop() || '').toLowerCase()
}

function partitionFiles(files: File[]): { spreadsheets: File[]; scans: File[]; allSupported: boolean } {
  const spreadsheets = files.filter((file) => SPREADSHEET_EXT.includes(ext(file)))
  const scans = files.filter((file) => SCAN_EXT.includes(ext(file)))
  return {
    spreadsheets,
    scans,
    allSupported: spreadsheets.length + scans.length === files.length,
  }
}

export function classifyImportForObject(objectKind: ObjectImportKind, files: File[]): TypedImportRoute {
  if (objectKind === 'other') return { kind: 'unsupported-object' }
  if (!files.length) return { kind: 'empty' }

  const { spreadsheets, scans, allSupported } = partitionFiles(files)

  if (objectKind === 'catalog') {
    if (spreadsheets.length === 1 && scans.length === 0 && files.length === 1) {
      return { kind: 'catalog-spreadsheet', spreadsheet: spreadsheets[0] }
    }
    return { kind: 'catalog-scans-rejected' }
  }

  if (files.length > 1) return { kind: 'document-multi-rejected' }
  return { kind: 'document-file', file: files[0] }
}

export function classifyImportFiles(files: File[]): ImportRoute {
  if (!files.length) return { kind: 'empty' }
  const { spreadsheets, scans, allSupported } = partitionFiles(files)
  if (!allSupported) return { kind: 'unsupported' }
  if (spreadsheets.length > 0 && scans.length > 0) return { kind: 'mixed' }
  if (spreadsheets.length > 1) return { kind: 'multiple-spreadsheets' }
  if (spreadsheets.length === 1) return { kind: 'spreadsheet', spreadsheet: spreadsheets[0] }
  return { kind: 'scans', scans }
}
